import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import {
	TenantContactDto,
	CreateCustomerDto,
	CustomerListDto,
	CustomerRecordDto,
	CustomersAppOptionsDto,
	CustomerSearchDto,
	CustomersFilters,
	CustomersStatsDto,
	CustomerStatusEnum,
	customerStatusToColor,
	customerValidators,
	TenantDriverDto,
	UpdateCustomerDto,
	ModulesEnum,
	CustomerTypeEnum,
	TagRecordCategoryEnum,
} from '@splitsecondconnect/ultramarine-shared';
import { plainToInstance } from 'class-transformer';

import { CommentsSectionComponent } from 'app/shared/components/comments-section/comments-section.component';

import { RecordTabsComponent } from '../../../../layout/components/record-tabs/record-tabs.component';
import { TabContentDirective } from '../../../../layout/components/record-tabs/tab-content.directive';
import { BaseRecordComponent } from '../../../../shared/base-classes/base-record.component';
import { AnnotationsSectionComponent } from '../../../../shared/components/annotations-section/annotations-section.component';
import { CommentsCardComponent } from '../../../../shared/components/comments-card/comments-card.component';
import { ContactCardComponent } from '../../../../shared/components/contact-card/contact-card.component';
import { ContextInfoComponent } from '../../../../shared/components/context-info/context-info.component';
import { DriverOverviewCardComponent } from '../../../../shared/components/driver-overview-card/driver-overview-card.component';
import { ErrorsBoxComponent } from '../../../../shared/components/errors-box/errors-box.component';
import { RecordFooterComponent } from '../../../../shared/components/footers/record-footer/record-footer.component';
import { RecordHeaderComponent } from '../../../../shared/components/headers/record-header/record-header.component';
import { HeaderDataModel } from '../../../../shared/components/headers/record-header/record-header.model';
import { MoreDetailsComponent } from '../../../../shared/components/more-details/more-details.component';
import { NoActivityComponent } from '../../../../shared/components/no-activity.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { TypeSectionComponent } from '../../../../shared/components/type-section/type-section.component';
import { UltraPrimaryButtonComponent } from '../../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import { SectionMetadataDirective } from '../../../../shared/directives/section-metadata.directive';
import { ADMINISTRATION_RECORD_TABS } from '../../../../shared/enums/administration-record-tabs';
import { generateInitials } from '../../../../shared/functions/generate-initials.function';
import {
	mapBaseDtoToBaseModel,
	mapBaseModelToBaseDto,
} from '../../../../shared/mapper/base.mapper';
import {
	mapContactDtoToModel,
	mapContactModelToDto,
} from '../../../../shared/mapper/contact-info.mapper';
import {
	mapDriverDtoToModel,
	mapDriverModelToDto,
} from '../../../../shared/mapper/driver.mapper';
import {
	mapFilesDtoToModel,
	mapFilesModelToDto,
} from '../../../../shared/mapper/files.mapper';
import {
	mapTagDtoToModel,
	mapTagModelToDto,
} from '../../../../shared/mapper/tags.mapper';
import { GetIconFromOptionsPipe } from '../../../../shared/pipes/get-icon-from-options.pipe';
import { MODULE_SELECTOR_STRATEGY_TOKEN } from '../../../../shared/tokens/module-selector.token';
import { TAGS_RECORD_CATEGORY_TOKEN } from '../../../../shared/tokens/tags-record-category.token';
import { CustomersListItemMenuComponent } from '../components/customers-list-item-menu/customers-list-item-menu.component';
import { CustomerModuleSelectorStrategy } from '../config/customer-module-selector-strategy';
import { CustomersService } from '../customers.service';
import { CustomerRecordModel } from '../models/customer-record.model';
import { CUSTOMER_TYPES } from '../translations/customers-select-items';

import { CustomersContactCompanyComponent } from './components/customers-contact-company/customers-contact-company.component';
import { CustomersContactIndividualComponent } from './components/customers-contact-individual/customers-contact-individual.component';
import { CustomersDriverComponent } from './components/customers-driver/customers-driver.component';
import { CustomersSummaryComponent } from './components/customers-summary/customers-summary.component';

@Component({
	selector: 'app-customers-record',
	templateUrl: 'customers-record.component.html',
	host: {
		'[class]': 'modalClass()',
	},
	imports: [
		CustomersSummaryComponent,
		CustomersContactCompanyComponent,
		CustomersContactIndividualComponent,
		CustomersDriverComponent,
		CustomersListItemMenuComponent,
		AnnotationsSectionComponent,
		RecordTabsComponent,
		RecordHeaderComponent,
		RecordFooterComponent,
		AsyncPipe,
		TabContentDirective,
		IonicModule,
		MoreDetailsComponent,
		NoActivityComponent,
		ContactCardComponent,
		DriverOverviewCardComponent,
		TypeSectionComponent,
		CommentsSectionComponent,
		StepperComponent,
		SectionMetadataDirective,
		ContextInfoComponent,
		ErrorsBoxComponent,
		UltraPrimaryButtonComponent,
		NgTemplateOutlet,
		CommentsCardComponent,
		GetIconFromOptionsPipe,
	],
	providers: [
		{
			provide: MODULE_SELECTOR_STRATEGY_TOKEN,
			useClass: CustomerModuleSelectorStrategy,
		},
		{
			provide: TAGS_RECORD_CATEGORY_TOKEN,
			useValue: TagRecordCategoryEnum.CUSTOMER,
		},
	],
})
export class CustomersRecordComponent extends BaseRecordComponent<
	CustomerRecordDto,
	CreateCustomerDto,
	UpdateCustomerDto,
	CustomerListDto,
	CustomersStatsDto,
	CustomersAppOptionsDto,
	CustomersFilters,
	CustomerRecordModel,
	CustomerStatusEnum,
	CustomerSearchDto
> {
	constructor(
		private _customersService: CustomersService,
		private _activatedRoute: ActivatedRoute,
		private _router: Router,
		private _changeDetectorRef: ChangeDetectorRef,
	) {
		super(_customersService, _router, _activatedRoute, _changeDetectorRef);
		this.module = ModulesEnum.CUSTOMERS;
		this.isFlowMode.set(true);
	}

	recordTabs = ADMINISTRATION_RECORD_TABS;
	customerType = CustomerTypeEnum;
	typeOptions = CUSTOMER_TYPES;

	initials = computed(() => {
		return generateInitials(
			undefined,
			this.recordData().contactFirstName,
			this.recordData().contactLastName,
		);
	});

	protected _isRecordFormValid(): boolean {
		const validationResult = customerValidators.canCreateOrUpdate(
			this._mapModelToDto(this.recordData()),
		);
		this.errorMessages.next(validationResult.errors);
		return validationResult.isValid;
	}

	protected _updateHeaderData(
		record: CustomerRecordModel,
	): HeaderDataModel<CustomerStatusEnum> {
		return {
			...this._updateHeaderDataBase(record),
			title:
				record.type === CustomerTypeEnum.INDIVIDUAL
					? (record.contactFirstName ?? '') +
						' ' +
						(record.contactLastName ?? '')
					: (record.contactCompanyName ?? ''),
			status: record.status,

			color: customerStatusToColor(record.status),
		};
	}

	protected _mapDtoToModel(dto: CustomerRecordDto): CustomerRecordModel {
		const contactInstance = plainToInstance(TenantContactDto, dto.contact);
		const driverInstance = plainToInstance(TenantDriverDto, dto.driver);
		const model: CustomerRecordModel = {
			...mapBaseDtoToBaseModel(dto),
			...mapTagDtoToModel(dto.tags),
			...mapContactDtoToModel(contactInstance),
			...mapDriverDtoToModel(driverInstance),
			notes: dto.notes ?? null,
			code: dto.code ?? null,
			type: dto.type ?? null,
			status: dto.status ?? null,
			...mapFilesDtoToModel(dto.files),
		};
		return model;
	}

	protected _mapModelToDto(model: CustomerRecordModel): CustomerRecordDto {
		return {
			...mapBaseModelToBaseDto(model),
			...mapContactModelToDto(model),
			...mapTagModelToDto(model.tags),
			...mapDriverModelToDto(model),
			notes: model.notes ?? null,
			code: model.code ?? null,
			type: model.type ?? null,
			...mapFilesModelToDto(model.files),
			status: model.status ?? null,
		};
	}

	protected _mapModelToCreateDto(
		model: CustomerRecordModel,
	): CreateCustomerDto {
		return {
			...mapBaseModelToBaseDto(model),
			...mapContactModelToDto(model),
			...mapTagModelToDto(model.tags),
			...mapDriverModelToDto(model),
			notes: model.notes ?? null,
			code: model.code ?? null,
			type: model.type ?? null,
			...mapFilesModelToDto(model.files),
		};
	}

	protected _mapModelToUpdateDto(
		model: CustomerRecordModel,
	): UpdateCustomerDto {
		return {
			...mapBaseModelToBaseDto(model),
			...mapContactModelToDto(model),
			...mapTagModelToDto(model.tags),
			...mapDriverModelToDto(model),
			notes: model.notes ?? null,
			code: model.code ?? null,
			type: model.type ?? null,
			...mapFilesModelToDto(model.files),
		};
	}
}
